import { createRouter, createWebHistory } from 'vue-router';
import Home from '../views/Home.vue';
// Components are now lazy loaded in the routes array

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      title: 'Finio Solutions - Complete Business Management Software | POS, CRM, KDS',
      description: 'Transform your business with Finio\'s all-in-one management solution. POS System, CRM, Kitchen Display, Delivery Management & more. Trusted in Morocco.',
    },
  },
  {
    path: '/products',
    name: 'Products',
    component: () => import('../views/Products.vue'),
    meta: {
      title: 'Our Products - POS, CRM, KDS, Delivery | Finio Solutions',
      description: 'Explore Finio\'s product suite: Point of Sale, Customer Management, Kitchen Display, Delivery System. Complete business solutions for restaurants and retail.',
    },
  },
  {
    path: '/subscriptions',
    name: 'Subscriptions',
    component: () => import('../views/Subscriptions.vue'),
    meta: {
      title: 'Pricing & Subscription Plans | Finio Solutions',
      description: 'Choose the perfect plan for your business. Flexible pricing starting from 200 MAD/month. Basic, Pro, and Custom plans available.',
    },
  },
  {
    path: '/hardware',
    name: 'Hardware',
    component: () => import('../views/Hardware.vue'),
    meta: {
      title: 'POS Hardware Packs in Morocco | Finio Solutions',
      description: 'Complete point-of-sale hardware packs delivered anywhere in Morocco: touch screens, printers, scanners, cash drawers, PCs and all-in-one terminals.',
    },
  },
  {
    path: '/hardware/order',
    name: 'HardwareOrder',
    component: () => import('../views/HardwareOrder.vue'),
    meta: {
      title: 'Configure Your Hardware Pack | Finio Solutions',
      description: 'Choose scanner, software subscription and payment (card, cash on delivery or bank transfer) for your Finio hardware pack.',
    },
  },
  {
    path: '/features',
    name: 'Features',
    component: () => import('../views/Features.vue'),
    meta: {
      title: 'Core Features - Complete Business Suite | Finio Solutions',
      description: 'Discover all Finio features: Inventory Management, Multi-location Support, Analytics, Customer Loyalty, Employee Management and more.',
    },
  },
  {
    path: '/coming-soon',
    name: 'ComingSoon',
    component: () => import('../views/ComingSoon.vue'),
    meta: {
      title: 'Future Innovations - Smart Store & Smart Airbnb | Finio Solutions',
      description: 'Be the first to experience Finio\'s upcoming innovations: Smart Store with AI monitoring and Smart Airbnb with automated management.',
    },
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/Contact.vue'),
    meta: {
      title: 'Contact Us - Get in Touch | Finio Solutions',
      description: 'Contact Finio Solutions for support, inquiries, or demo requests. Located in Tétouan, Morocco. Email: contact@finio.pro, Phone: +212 664 241 482',
    },
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: {
      title: 'About Us - Finio Solutions Story & Team | Finio Solutions',
      description: 'Learn about Finio Solutions - our mission, vision, values, and the team behind the complete business management platform. Serving businesses across Morocco.',
    },
  },
  {
    path: '/help-center',
    name: 'HelpCenter',
    component: () => import('../views/HelpCenter.vue'),
    meta: {
      title: 'Help Center - Support & FAQs | Finio Solutions',
      description: 'Find answers to common questions about Finio products, features, subscriptions, and technical support.',
    },
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/Terms.vue'),
    meta: {
      title: 'Terms & Conditions | Finio Solutions',
      description: 'Read Finio Solutions\' terms and conditions for using our POS, CRM, and business management software services.',
    },
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/Privacy.vue'),
    meta: {
      title: 'Privacy Policy | Finio Solutions',
      description: 'Learn how Finio Solutions protects your data and privacy. Our commitment to data security and user privacy.',
    },
  },
  {
    path: '/checkout',
    name: 'Checkout',
    component: () => import('../views/Checkout.vue'),
    meta: {
      title: 'Subscription Checkout | Finio Solutions',
      description: 'Complete your Finio subscription purchase. Secure checkout with multiple payment options. Get started today.',
    },
  },
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: () => import('../views/PaymentSuccess.vue'),
    meta: {
      title: 'Payment Successful | Finio Solutions',
      description: 'Your Finio subscription payment was completed successfully.',
    },
  },
  {
    path: '/payment/failed',
    name: 'PaymentFailed',
    component: () => import('../views/PaymentFailed.vue'),
    meta: {
      title: 'Payment Failed | Finio Solutions',
      description: 'Your Finio subscription payment could not be completed. Please try again.',
    },
  },
  {
    path: '/launch',
    name: 'LaunchLanding',
    component: () => import('../views/LaunchLanding.vue'),
    meta: {
      title: 'Finio Pro - Early Access',
      description: 'Join Finio Pro as a Founding Member. Get 3 months free access to the next generation of business management tools.',
    },
  },
  {
    path: '/faq',
    name: 'FAQ',
    component: () => import('../views/FAQ.vue'),
    meta: {
      title: 'Frequently Asked Questions | Finio Solutions',
      description: 'Find answers to common questions about Finio POS, subscriptions, hardware, and offline mode.',
    },
  },

  {
    path: '/changelog',
    name: 'Changelog',
    component: () => import('../views/Changelog.vue'),
    meta: {
      title: 'Changelog & Updates | Finio Solutions',
      description: 'See the latest features, improvements, and fixes in Finio POS and Management Platform.',
    },
  },
  {
    path: '/docs',
    component: () => import('../views/docs/DocsLayout.vue'),
    children: [
      {
        path: 'getting-started',
        name: 'DocsGettingStarted',
        component: () => import('../views/docs/GettingStarted.vue'),
        meta: { title: 'Getting Started Guide | Finio Docs' }
      },
      {
        path: 'pos-usage',
        name: 'DocsPOSUsage',
        component: () => import('../views/docs/POSUsage.vue'),
        meta: { title: 'POS Usage Manual | Finio Docs' }
      },
      {
        path: 'kds-workflow',
        name: 'DocsKDSWorkflow',
        component: () => import('../views/docs/KDSWorkflow.vue'),
        meta: { title: 'KDS Workflow Guide | Finio Docs' }
      },
      {
        path: 'pms-overview',
        name: 'DocsPMSOverview',
        component: () => import('../views/docs/PMSOverview.vue'),
        meta: { title: 'PMS & Hotel Management | Finio Docs' }
      },
      {
        path: 'offline-sync',
        name: 'DocsOfflineSync',
        component: () => import('../views/docs/OfflineSync.vue'),
        meta: { title: 'Offline Mode & Sync | Finio Docs' }
      },
      {
        path: 'roles-permissions',
        name: 'DocsRolesPermissions',
        component: () => import('../views/docs/RolesPermissions.vue'),
        meta: { title: 'Roles & Permissions | Finio Docs' }
      },
      {
        path: 'finance-closures',
        name: 'DocsFinanceClosures',
        component: () => import('../views/docs/FinanceClosures.vue'),
        meta: { title: 'Invoices, Refunds & Closures | Finio Docs' }
      },
      {
        path: 'hardware-printers',
        name: 'DocsHardwarePrinters',
        component: () => import('../views/docs/HardwarePrinters.vue'),
        meta: { title: 'Printers & Hardware | Finio Docs' }
      },
      {
        path: 'integrations',
        name: 'DocsIntegrations',
        component: () => import('../views/docs/Integrations.vue'),
        meta: { title: 'Integrations | Finio Docs' }
      },
      {
        path: 'compliance',
        name: 'DocsCompliance',
        component: () => import('../views/docs/Compliance.vue'),
        meta: { title: 'Compliance & Fiscal Notes | Finio Docs' }
      },
      {
        path: 'troubleshooting',
        name: 'DocsTroubleshooting',
        component: () => import('../views/docs/Troubleshooting.vue'),
        meta: { title: 'Troubleshooting Guide | Finio Docs' }
      },
      {
        path: 'security',
        name: 'DocsSecurity',
        component: () => import('../views/docs/Security.vue'),
        meta: {
          title: 'Security Overview | Finio Docs',
          description: 'Learn about Finio\'s enterprise-grade security, data protection, and compliance standards.',
        },
      },
    ]
  },
  // 404 Catch-all (MUST be last)
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
    meta: {
      title: 'Page Not Found | Finio Solutions',
      description: 'The page you are looking for could not be found.',
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top when navigating to a new page
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, behavior: 'smooth' };
    }
  },
});

// Update document title and meta tags on route change
router.afterEach((to) => {
  // Update title
  if (to.meta.title) {
    document.title = to.meta.title;
  }

  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  if (to.meta.description) {
    metaDescription.setAttribute('content', to.meta.description);
  }

  // Update Open Graph meta tags
  let ogTitle = document.querySelector('meta[property="og:title"]');
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  if (to.meta.title) {
    ogTitle.setAttribute('content', to.meta.title);
  }

  let ogDescription = document.querySelector('meta[property="og:description"]');
  if (!ogDescription) {
    ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    document.head.appendChild(ogDescription);
  }
  if (to.meta.description) {
    ogDescription.setAttribute('content', to.meta.description);
  }

  // Update Twitter meta tags
  let twitterTitle = document.querySelector('meta[property="twitter:title"]');
  if (!twitterTitle) {
    twitterTitle = document.createElement('meta');
    twitterTitle.setAttribute('property', 'twitter:title');
    document.head.appendChild(twitterTitle);
  }
  if (to.meta.title) {
    twitterTitle.setAttribute('content', to.meta.title);
  }

  let twitterDescription = document.querySelector('meta[property="twitter:description"]');
  if (!twitterDescription) {
    twitterDescription = document.createElement('meta');
    twitterDescription.setAttribute('property', 'twitter:description');
    document.head.appendChild(twitterDescription);
  }
  if (to.meta.description) {
    twitterDescription.setAttribute('content', to.meta.description);
  }
});

export default router;
